/**
 * Календарная дата без времени и без часового пояса.
 *
 * Почему не Date: «до зарплаты 18 дней» и «покупка попала в расчётный период
 * кредитки» — вычисления по календарю, а не по моменту времени. Date тянет за
 * собой часовой пояс и переходы на летнее время, и ошибка на сутки здесь
 * означает неверный резерв под платёж. Дата-строка от этого класса ошибок
 * свободна по построению.
 */

declare const DateBrand: unique symbol;
export type PlainDate = string & { readonly [DateBrand]: true };

const RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function pd(input: string): PlainDate {
  const m = RE.exec(input);
  if (!m) throw new TypeError(`PlainDate: ожидал YYYY-MM-DD, получил «${input}»`);
  const [, y, mo, d] = m;
  const year = Number(y), month = Number(mo), day = Number(d);
  if (month < 1 || month > 12) throw new TypeError(`PlainDate: месяц ${month}`);
  if (day < 1 || day > daysInMonth(year, month)) throw new TypeError(`PlainDate: день ${input}`);
  return input as PlainDate;
}

export function ymd(year: number, month: number, day: number): PlainDate {
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return pd(`${p(year, 4)}-${p(month)}-${p(day)}`);
}

export const parts = (d: PlainDate): { year: number; month: number; day: number } => {
  const m = RE.exec(d)!;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
};

export const daysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

const toUtc = (d: PlainDate): number => {
  const { year, month, day } = parts(d);
  return Date.UTC(year, month - 1, day);
};
const fromUtc = (ms: number): PlainDate => {
  const dt = new Date(ms);
  return ymd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
};

export const addDays = (d: PlainDate, n: number): PlainDate => fromUtc(toUtc(d) + n * 86_400_000);

const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/**
 * Дата словами для текстов, которые видит человек: «9 октября», а с другим
 * годом — «5 января 2027». «2026-10-09» в подсказке приходится расшифровывать.
 */
export const dayWords = (d: PlainDate, today?: PlainDate): string => {
  const { year, month, day } = parts(d);
  const word = `${day} ${MONTHS_GEN[month - 1]}`;
  return today && today.slice(0, 4) === d.slice(0, 4) ? word : `${word} ${year}`;
};
export const diffDays = (from: PlainDate, to: PlainDate): number =>
  Math.round((toUtc(to) - toUtc(from)) / 86_400_000);
export const cmp = (a: PlainDate, b: PlainDate): number => (a < b ? -1 : a > b ? 1 : 0);
export const isBefore = (a: PlainDate, b: PlainDate): boolean => a < b;
export const isAfter = (a: PlainDate, b: PlainDate): boolean => a > b;
export const minDate = (...ds: PlainDate[]): PlainDate => ds.reduce((a, b) => (a <= b ? a : b));
export const maxDate = (...ds: PlainDate[]): PlainDate => ds.reduce((a, b) => (a >= b ? a : b));

/** 0 — воскресенье, 6 — суббота. */
export const weekday = (d: PlainDate): number => new Date(toUtc(d)).getUTCDay();
export const isWeekend = (d: PlainDate): boolean => weekday(d) === 0 || weekday(d) === 6;

/**
 * Дата в месяце с зажимом: 31-е в феврале превращается в 28/29-е.
 * Без этого обязательство «31 числа» пропадает в короткие месяцы.
 */
export function withDayClamped(d: PlainDate, day: number): PlainDate {
  const { year, month } = parts(d);
  return ymd(year, month, Math.min(day, daysInMonth(year, month)));
}

export function addMonths(d: PlainDate, n: number): PlainDate {
  const { year, month, day } = parts(d);
  const total = year * 12 + (month - 1) + n;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return ymd(y, m, Math.min(day, daysInMonth(y, m)));
}

export type ShiftRule = 'exact' | 'previous_business_day' | 'next_business_day';

/** Перенос даты платежа или зарплаты с выходных. */
export function applyShift(d: PlainDate, rule: ShiftRule): PlainDate {
  if (rule === 'exact') return d;
  let out = d;
  const step = rule === 'previous_business_day' ? -1 : 1;
  while (isWeekend(out)) out = addDays(out, step);
  return out;
}

/**
 * Ближайшее наступление «числа месяца» строго после `after`.
 * Используется и для зарплаты, и для обязательств, и для платежа по кредитке.
 */
export function nextOccurrence(
  after: PlainDate,
  dayOfMonth: number,
  rule: ShiftRule = 'exact',
): PlainDate {
  for (let i = 0; i <= 2; i++) {
    const candidate = applyShift(withDayClamped(addMonths(after, i), dayOfMonth), rule);
    if (isAfter(candidate, after)) return candidate;
  }
  throw new Error(`nextOccurrence: не нашёл дату для ${dayOfMonth} после ${after}`);
}

/**
 * Ближайшее наступление «числа месяца» не позже указанной даты.
 *
 * Нужно для определения текущего цикла: платёж 5-го числа, когда сегодня 9-е,
 * относится к этому циклу, а не к следующему — и пока он не подтверждён,
 * резерв под него держится.
 */
export function prevOccurrenceInclusive(
  from: PlainDate,
  dayOfMonth: number,
  rule: ShiftRule = 'exact',
): PlainDate {
  for (let i = 0; i <= 2; i++) {
    const candidate = applyShift(withDayClamped(addMonths(from, -i), dayOfMonth), rule);
    if (!isAfter(candidate, from)) return candidate;
  }
  throw new Error(`prevOccurrenceInclusive: не нашёл дату для ${dayOfMonth} до ${from}`);
}

/** То же, но включая сегодняшний день. */
export function nextOccurrenceInclusive(
  from: PlainDate,
  dayOfMonth: number,
  rule: ShiftRule = 'exact',
): PlainDate {
  const yesterday = addDays(from, -1);
  return nextOccurrence(yesterday, dayOfMonth, rule);
}

/**
 * Календарная дата из момента времени по локальному календарю устройства.
 *
 * Берём именно локальные геттеры, а не UTC: «сегодня» для пользователя — это
 * его сегодня. В 01:00 по Москве UTC-дата всё ещё предыдущая, и расход,
 * занесённый ночью, уехал бы во вчерашний день вместе с бюджетом.
 */
export function plainDateFromLocal(d: Date): PlainDate {
  return ymd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}
